import { pathToAction } from 'redux-first-router';
import * as Sentry from '@sentry/browser';

import { fetchCheckout, fetchData } from '../utils/async';
import { getCookie, removeEvent, addEvent } from '../utils/dom';
import { deepClone, deepCompare } from '../utils/object';
import { prefix, translateObjectData } from '../utils/locale';
import { fieldValidation } from '../utils/string';
import { paymentMethodsMapping } from '../config';

export const SUBSCRIBED = 'SUBSCRIBED';
export const RECENTLY_VIEWED = 'RECENTLY_VIEWED';
export const LIST_ITEM_CACHE = 'LIST_ITEM_CACHE';
export const ADD_VOUCHER = 'ADD_VOUCHER';
export const VOUCHER_INPUT = 'VOUCHER_INPUT';
export const VOUCHER_ERROR = 'VOUCHER_ERROR';
export const ADD_TO_BAG_ERROR = 'ADD_TO_BAG_ERROR';
export const SELECT_SIZE = 'SELECT_SIZE';
export const SELECT_QUANTITY = 'SELECT_QUANTITY';
export const UPDATE_SELECTION_CACHE = 'UPDATE_SELECTION_CACHE';
export const UPDATE_GIFT_CARD_AMOUNT = 'UPDATE_GIFT_CARD_AMOUNT';
export const UPDATE_SELECTION = 'UPDATE_SELECTION';
export const UPDATE_ADDITIONAL_INFORMATION = 'UPDATE_ADDITIONAL_INFORMATION';
export const UPDATE_BUDBEE = 'UPDATE_BUDBEE';
export const GET_ADDRESS = 'GET_ADDRESS';
export const UPDATE_ADDRESS = 'UPDATE_ADDRESS';
export const UPDATE_ERRORS = 'UPDATE_ERRORS';
export const DELIVERY_PROCEED = 'DELIVERY_PROCEED';
export const KLARNA_LOADED = 'KLARNA_LOADED';
export const KLARNA_UNLOADED = 'KLARNA_UNLOADED';
export const INGRID_LOADED = 'INGRID_LOADED';
export const INGRID_UNLOADED = 'INGRID_UNLOADED';
export const ADYEN_INITIATED = 'ADYEN_INITIATED';
export const STRIPE_INITIATED = 'STRIPE_INITIATED';
export const STRIPE_PAYMENT_INTENTS_INITIATED = 'STRIPE_PAYMENT_INTENTS_INITIATED';
export const PAYMENT_UPDATING = 'PAYMENT_UPDATING';
export const SELECTION_ERROR = 'SELECTION_ERROR';
export const SELECTION_ERROR_STOCK = 'SELECTION_ERROR_STOCK';
export const SELECTION_ERROR_PASSWORD = 'SELECTION_ERROR_PASSWORD';
export const SELECTION_ERROR_REGISTERED = 'SELECTION_ERROR_REGISTERED';
export const ENRICH_PRODUCTS = 'ENRICH_PRODUCTS';
export const INITIATED_SCRIPT = 'INITIATED_SCRIPT';
export const BOOLEAN_CHANGE = 'BOOLEAN_CHANGE';
export const PAYMENT_RETURN = 'PAYMENT_RETURN';
export const DESTROY_SELECTION = 'DESTROY_SELECTION';

const removeInvalidPaymentFields = (setup, paymentData, useShippingAddress, keepPassword = false) => {
	const whitelistedFields = {
		level0: ['paymentMethod', 'shippingMethod', 'consents', 'affiliate', 'additionalNotes', 'additionalInformation', 'customerClubFields', 'cartAbandonmentEmail', 'accountNumber', 'additionalFields', 'paymentReturnPage', 'paymentFailedPage', 'termsAndConditions', 'address', 'shippingAddress'],
		address: ['newsletter', 'firstName', 'lastName', 'email', 'password', 'phoneNumber', 'company', 'vatNumber', 'address1', 'address2', 'zipCode', 'state', 'city', 'country'],
		shippingAddress: ['firstName', 'lastName', 'email', 'phoneNumber', 'company', 'address1', 'address2', 'zipCode', 'city', 'state', 'country'],
	};

	// Copy only whitelisted fields.
	const returnData = Object.keys(paymentData).reduce((acc, f) => {
		if (whitelistedFields.level0.indexOf(f) === -1) return acc;
		if (whitelistedFields[f]) {
			// Add shippingAddress only if exists
			if (f === 'shippingAddress' && !useShippingAddress) return acc;

			// check second level.
			return {
				...acc,
				[f]: Object.keys(paymentData[f]).reduce((acc1, f1) => {
					if (whitelistedFields[f].indexOf(f1) === -1 || (!keepPassword && f1 === 'password')) return acc1;
					return { ...acc1, [f1]: paymentData[f][f1] };
				}, {}),
			};
		}
		if (typeof paymentData[f] === 'object') return { ...acc, [f]: deepClone(paymentData[f]) };
		return { ...acc, [f]: paymentData[f] };
	}, {});

	// Add required fields
	if (useShippingAddress) {
		returnData.shippingAddress.country = returnData.shippingAddress.country || setup.country;
		if (!returnData.shippingAddress.phoneNumber && returnData.address.phoneNumber) {
			returnData.shippingAddress.phoneNumber = returnData.address.phoneNumber;
		}
	}
	returnData.address.country = returnData.address.country || setup.country;

	let additionalNotes = '';
	if (returnData.additionalInformation) {
		additionalNotes = returnData.additionalInformation.map((info) => info?.message).join('\n');
		delete returnData.additionalInformation;
	}
	if (additionalNotes) returnData.additionalNotes = additionalNotes;

	// Check that state is correct and applicable else remove.
	if (!(returnData.address.state && setup.countries?.[returnData.address.country]?.states?.[returnData.address.state])) delete returnData.address.state;
	// Check that state is correct and applicable else remove.
	if (useShippingAddress && !(returnData.shippingAddress.state && setup.countries?.[returnData.shippingAddress.country]?.states?.[returnData.shippingAddress.state])) delete returnData.shippingAddress.state;

	returnData.paymentFailedPage = `${window.location.origin}${prefix(setup, 'selection/failed')}`;
	returnData.paymentReturnPage = `${window.location.origin}${prefix(setup, 'selection/receipt')}`;

	return returnData;
};

export const clientInit = () => ({ type: 'CLIENT_INIT' });
export const retrieveProductCache = () => ({ type: 'RETRIEVE_PRODUCT_CACHE' });

export const subscribeToNewsletter = ({ email, interest }) => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	const { country, language } = getState().environment.setup;
	const postData = { country, language };
	if (interest !== '') postData.gender = interest === 'men' ? 'male' : 'female';

	return fetchCheckout(`/newsletter-subscription/${email}`, token, postData, 'POST').then(() => (
		dispatch({ type: SUBSCRIBED })
	)).catch((err) => console.log('DEBUG: error when subscribing to newsletter ', err));
};

export const productAlert = ({ selected }) => (dispatch, getState) => {
	const { newsletter, email } = getState().checkout.payment.address;
	const { token } = getState().checkout.selection;
	const { setup } = getState().environment;
	const { country, language } = setup;
	if (newsletter) dispatch(subscribeToNewsletter({ email }));

	return fetchCheckout('/back-in-stock-subscription', token, { email, shipTo: { country }, item: selected, language }, 'POST').then(() => (
		dispatch({ type: SUBSCRIBED })
	)).catch((err) => console.log('DEBUG: error when subscribing to newsletter ', err));
};

export const selectSize = (product, item, quantity = 1) => ({ type: SELECT_SIZE, product, item, quantity });
export const addRecentlyViewed = (product) => ({ type: RECENTLY_VIEWED, product });
export const updateListItemCache = (productId, listName) => ({ type: LIST_ITEM_CACHE, productId, listName });

export const addToBag = (item, quantity = 1, category, cachedItem, note = false) => (dispatch, getState) => {
	const { setup } = getState().environment;
	const { token } = getState().checkout.selection;
	const product = item.slice(0, item.indexOf('-'));
	const cache = deepClone(cachedItem);
	cache.category = category;

	return (
		fetchCheckout(`/items/${item}`, token, { quantity, productUrl: `${window.location.origin}${prefix(setup, cache.canonicalUri)}` }, 'POST').then((data) => {
			if (!data || data?.errors) return dispatch({ type: ADD_TO_BAG_ERROR });

			dispatch({ type: UPDATE_SELECTION_CACHE, product, productCache: cache });
			if (note) dispatch({ type: UPDATE_ADDITIONAL_INFORMATION, note });

			return dispatch({ type: UPDATE_SELECTION, data });
		}).then((response) => {
			setTimeout(() => {
				if (getState().environment.properties.openCart === 1) dispatch({ type: 'OPEN_CART' });
			}, 3000);

			setTimeout(() => {
				if (getState().environment.properties.openCart === 2) dispatch({ type: 'OPEN_CART' });
			}, 3300);

			dispatch({ type: 'OPEN_CART' });

			return response;
		}).catch((err) => console.log('DEBUG: err', err))
	);
};

export const changeQuantity = ({ item, line }, quantity) => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	const personalizedItems = getState().checkout.payment.additionalInformation;
	const product = item.slice(0, item.indexOf('-'));
	const allItems = getState().checkout.selection.items;
	// Get all items sharing the same product id before removing the product from cache.
	const totalProducts = allItems.reduce((acc, selectionItem) => (selectionItem.item.indexOf(product) === 0 ? acc + 1 : acc), 0);

	return (
		fetchCheckout(`/lines/${line}/quantity/${quantity}`, token, {}, 'PUT').then((data) => {
			const personalizedItem = personalizedItems.filter((pI) => pI.item === item)[0];
			if (personalizedItem) {
				// There is personalization attached to this item run changeQuantity to match.
				const personalizedSelectionItem = allItems.filter((aI) => aI.item === personalizedItem.details.embossingItem)[0];
				if (!personalizedSelectionItem) dispatch({ type: UPDATE_ADDITIONAL_INFORMATION, note: { item, message: '' } });
				else {
					if (quantity === 0) {
						dispatch({ type: UPDATE_ADDITIONAL_INFORMATION, note: { item, message: '' } });
					}
					dispatch(changeQuantity(personalizedSelectionItem, quantity));
				}
			}

			if (quantity === 0 && totalProducts <= 1) {
				// Only one item left before updating selection.
				dispatch({ type: UPDATE_SELECTION_CACHE, product });
			}

			return dispatch({ type: UPDATE_SELECTION, data });
		}).catch((err) => console.log('DEBUG: err', err))
	);
};

export const changeSize = (product, line, quantity, newItem) => (dispatch, getState) => {
	const { token } = getState().checkout.selection;

	// Remove previous product
	return fetchCheckout(`/lines/${line}/quantity/0`, token, {}, 'PUT').then(() => (
		fetchCheckout(`/items/${newItem}/quantity/${quantity}`, token, {}, 'POST')
	)).then((data) => (
		dispatch({ type: UPDATE_SELECTION, data })
	)).catch((err) => console.log('DEBUG: err', err));
};

export const forgot = () => (dispatch, getState) => {
	const { setup } = getState().environment;
	const { token } = getState().checkout.selection;
	const { email } = getState().checkout.payment.address;

	return (
		fetchCheckout(`/password-reset-email/${email}`, token, { linkUri: prefix(setup, 'remember') }, 'POST')
			.then((data) => (data.errors ? Promise.reject(new Error('Password reset action error.')) : data))
			.then(() => dispatch({ type: 'ACCOUNT_FORGOT_SENT' }))
			.catch(() => dispatch({ type: 'ACCOUNT_FORGOT_FAILED', hasError: true }))
	);
};

export const resetPassword = () => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	const { passwordNew } = getState().checkout.payment.address;
	const { getParameters = {}, setup = {} } = getState().environment;
	const { i = '', id = '' } = getParameters;
	const postData = {
		i,
		id,
		newPassword: passwordNew,
	};

	return (
		fetchCheckout('/password-reset', token, postData, 'POST').then((accountData) => {
			dispatch({ type: 'UPDATE_ACCOUNT', data: accountData });
			return dispatch({ type: 'ACCOUNT', payload: { language: setup.language } });
		}).catch(() => dispatch({ type: 'ACCOUNT_ENTRY_FAILED', hasError: true }))
	);
};

export const login = () => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	const { password, email } = getState().checkout.payment.address;

	return (
		fetchCheckout(`/login/${email}`, token, { password }, 'POST').then((accountData) => {
			if (accountData.errors && accountData.errors.password) return dispatch({ type: SELECTION_ERROR_PASSWORD, hasError: true });
			return dispatch({ type: 'UPDATE_ACCOUNT', data: accountData });
		}).catch(() => dispatch({ type: 'ACCOUNT_ENTRY_FAILED', hasError: true }))
	);
};

export const register = () => (dispatch, getState) => {
	const { payment, selection } = getState().checkout;
	const { setup } = getState().environment;
	const { token } = selection;
	const cleanData = removeInvalidPaymentFields(setup, payment, false, 'selection', true);
	if (cleanData.address) delete cleanData.address.company;

	return (
		fetchCheckout('/register', token, { ...cleanData.address }, 'POST').then((accountData) => {
			if (accountData.errors) {
				if (accountData.errors.email) return dispatch({ type: SELECTION_ERROR_REGISTERED, hasError: true });
				return dispatch({ type: 'ACCOUNT_ENTRY_FAILED', hasError: true });
			}
			return dispatch({ type: 'UPDATE_ACCOUNT', data: accountData });
		}).catch(() => dispatch({ type: 'ACCOUNT_ENTRY_FAILED', hasError: true }))
	);
};

export const accountOrders = () => (dispatch, getState) => {
	const { selection, account } = getState().checkout;
	const { token } = selection;
	const { orders = [] } = account;

	return fetchCheckout('/orders', token, { size: 1, from: orders.length }, 'POST').then((data) => {
		dispatch({ type: 'UPDATE_ACCOUNT', data });
		return dispatch({ type: 'ACCOUNT_ORDERS', data });
	}).catch((err) => dispatch({ type: 'ACCOUNT_ORDERS_FAILED', err }));
};

export const updateAccountAddress = () => (dispatch, getState) => {
	const { selection, payment } = getState().checkout;
	const { setup } = getState().environment;
	const { token } = selection;
	const cleanData = removeInvalidPaymentFields(setup, payment, false, 'selection', true);
	const args = {};
	const errors = { address: {} };
	const requiredFields = [
		'firstName',
		'lastName',
		'address1',
		'zipCode',
		'city',
		'phoneNumber',
		'country',
	];
	if (cleanData.address.company) delete cleanData.address.company;
	if (cleanData.address.birthday) {
		cleanData.address.customerClubFields = { birthday: cleanData.address.birthday };
		delete cleanData.address.birthday;
	}


	Object.keys(cleanData.address).forEach((input) => {
		const value = cleanData.address[input];
		const checkStates = input === 'state' && setup.countries[cleanData.address.country].states;
		if (checkStates && !checkStates[value]) errors.address.state = 1;
		if (requiredFields.indexOf(input) !== -1 && fieldValidation(value, input)) errors.address[input] = 1;
		if (input.indexOf('password') === -1 && input.indexOf('email') === -1) {
			args[input] = value;
		}
	});

	if (!deepCompare(errors, { address: {} })) {
		dispatch({ type: 'FORM_ERRORS', errors });
		return Promise.reject(new Error('Update account address action failed.'));
	}

	return fetchCheckout('/customer/update', token, args, 'PUT').then((data) => (
		dispatch({ type: 'UPDATE_ACCOUNT', data })
	)).catch(() => dispatch({ type: 'UPDATE_ACCOUNT_FAILED' }));
};

export const updateAccountEmail = () => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	const { emailNew, emailNewRepeat } = getState().checkout.payment.address;
	const errors = { address: {} };

	if (fieldValidation(emailNew, 'email')) errors.address.emailNew = 1;
	if (emailNew !== emailNewRepeat) errors.address.emailNewRepeat = 1;

	if (!deepCompare(errors, { address: {} })) {
		return dispatch({ type: 'FORM_ERRORS', errors });
	}

	return fetchCheckout('/email', token, { newEmail: emailNew }, 'PUT').then((data) => {
		if (data.errors && data.errors.newEmail) return dispatch({ type: SELECTION_ERROR_REGISTERED, hasError: true });
		return dispatch({ type: 'UPDATE_ACCOUNT', data });
	}).catch(() => dispatch({ type: 'UPDATE_ACCOUNT_FAILED' }));
};

export const updateAccountPassword = () => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	const { passwordNew, passwordNewRepeat, passwordOld } = getState().checkout.payment.address;
	const errors = { address: {} };

	if (fieldValidation(passwordOld, 'password')) errors.address.passwordOld = 1;
	if (fieldValidation(passwordNew, 'password')) errors.address.passwordNew = 1;
	if (passwordNew !== passwordNewRepeat) errors.address.passwordNewRepeat = 1;

	if (!deepCompare(errors, { address: {} })) {
		dispatch({ type: 'FORM_ERRORS', errors });
		return Promise.reject(new Error('Update account password action failed.'));
	}

	return fetchCheckout('/password', token, { password: passwordOld, newPassword: passwordNew }, 'PUT').then((data) => {
		if (data.errors && data.errors.password) return dispatch({ type: SELECTION_ERROR_PASSWORD, hasError: true });
		return dispatch({ type: 'UPDATE_ACCOUNT', data });
	}).catch(() => dispatch({ type: 'UPDATE_ACCOUNT_FAILED' }));
};

export const updateAccountConsents = () => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	const { newsletter, firstName, lastName } = getState().checkout.payment.address;

	/*
		Centra requires sending in firstName and lastName for some reason.
		This can cause a small issue where the customer inadvertently updates
		their firstName and lastName in another form due to pulling the data
		from the store instead of the form.
		A small risk but no large consequences of doing so.
	*/
	return fetchCheckout('/customer/update', token, { newsletter, firstName, lastName }, 'PUT').then((data) => (
		dispatch({ type: 'UPDATE_ACCOUNT', data })
	)).catch(() => dispatch({ type: 'UPDATE_ACCOUNT_FAILED' }));
};

export const logoutAccount = () => (dispatch, getState) => {
	const { token } = getState().checkout.selection;

	return fetchCheckout('/logout', token, {}, 'POST').then(() => (
		dispatch({ type: 'ACCOUNT_LOGOUT' })
	)).catch(() => dispatch({ type: 'ACCOUNT_LOGOUT_FAILED' }));
};

export const createGiftCard = () => (dispatch, getState) => {
	const { country, pricelist, market, language, currency } = getState().environment.setup;
	const { type, amount } = getState().checkout.giftCard;
	const giftCardSelection = 'giftCardSelection';
	const { token = '' } = getState().checkout.giftCardSelection;

	// Fail early if parameters are missing.
	if (!(type && /\S/.test(type) && amount && /\S/.test(amount))) {
		return dispatch({ type: UPDATE_SELECTION, giftCardSelection, failed: true });
	}

	return fetchData('gift-card', { giftCard: type, amount, currency, country, pricelist, market, language }).then((giftData) => {
		if (giftData.data && !giftData.errors) {
			return fetchCheckout(`/selection/${giftData.data.selection}`, token, {}, 'PUT').then((data) => (
				dispatch({ type: UPDATE_SELECTION, data, giftCardSelection })
			)).catch((err) => console.log('DEBUG: err', err));
		}

		return dispatch({ type: UPDATE_SELECTION, giftCardSelection, failed: true });
	}).catch((err) => console.log(err));
};

export const updateGiftCardAmount = (giftCard, amount) => ({ type: UPDATE_GIFT_CARD_AMOUNT, giftCard, amount });

export const changeCountry = (country) => (dispatch, getState) => {
	const { routesMap } = getState().location;
	const { countries, language } = getState().environment.setup;
	const { paths = {} } = getState().pageStatus;
	const newCountry = countries[country];
	const market = getState().environment.campaign ? getState().environment.campaign.market : newCountry.market;
	const { token } = getState().checkout.selection;
	const { state = '' } = getState().checkout.payment.shippingAddress;
	dispatch({ type: 'UPDATE_COUNTRY', country: newCountry.country, currency: newCountry.currency, market, pricelist: newCountry.pricelist });
	dispatch({ type: 'RETRIEVE_PRODUCT_CACHE' });

	// Does language change when country changes?
	// If so update url
	if (newCountry.language !== language) {
		const changeToLanguage = newCountry.language.toLowerCase();
		dispatch({ type: 'CHANGE_LANGUAGE', language: changeToLanguage });
		// console.log('DEBUG: ', paths);
		// console.log('DEBUG: ', `/${changeToLanguage}/${paths && paths[changeToLanguage] ? paths[changeToLanguage] : ''}`);
		// console.log('DEBUG: ', pathToAction(`/${changeToLanguage}/${paths && paths[changeToLanguage] ? paths[changeToLanguage] : ''}`, routesMap));
		if (changeToLanguage !== 'en') {
			dispatch(pathToAction(`/${changeToLanguage}/${paths && paths[changeToLanguage] ? paths[changeToLanguage] : ''}`, routesMap));
		} else {
			dispatch(pathToAction(`/${paths && paths[changeToLanguage] ? paths[changeToLanguage] : ''}`, routesMap));
		}
	}

	return (
		fetchCheckout(`/countries/${newCountry.country}`, token, {}, 'PUT').then((data) => {
			if (newCountry?.states?.[state]) dispatch(changeState(state));
			return dispatch({ type: UPDATE_SELECTION, data, country: newCountry.country });
		}).then(({ data }) => {
			const hasKlarna = data.paymentMethods.filter((method) => method.paymentMethod === paymentMethodsMapping.klarnaCheckout) || [];
			if (getState().checkout.klarnaLoaded && hasKlarna.length > 0) window.location = window.location.href;
			return { data };
		}).catch((err) => console.log('DEBUG: err', err))
	);
};

export const changeState = (state, country = false) => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	if (!country) country = getState().environment.setup.country;
	return (
		fetchCheckout(`/countries/${country}/states/${state}`, token, [], 'PUT').then((data) => (
			dispatch({ type: UPDATE_SELECTION, data })
		)).catch((err) => {
			console.log('DEBUG: err', err);
		})
	);
};

export const changePaymentMethod = (method, selectionType = 'selection') => (dispatch, getState) => {
	const { token } = getState().checkout[selectionType];
	return (
		fetchCheckout(`/payment-methods/${method}`, token, {}, 'PUT').then((data) => (
			dispatch({ type: UPDATE_SELECTION, data, giftCardSelection: selectionType })
		)).catch((err) => console.log('DEBUG: err', err))
	);
};

export const changeShippingMethod = (method) => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	return (
		fetchCheckout(`/shipping-methods/${method}`, token, {}, 'PUT').then((data) => (
			dispatch({ type: UPDATE_SELECTION, data })
		)).catch((err) => console.log('DEBUG: err', err))
	);
};

export const checkZipCode = () => (dispatch, getState) => {
	const { zipCode = '', country } = getState().checkout.payment.address;
	const { selection } = getState().checkout;

	if (/\S/.test(zipCode)) {
		fetchData('budbee', { country, zipCode }).then(({ success, errors = false }) => {
			if (!success && selection.shippingMethod && selection.shippingMethod.toLowerCase().indexOf('budbee') !== -1) {
				const otherShippingMethods = selection.shippingMethods.filter((method) => method.shippingMethod.toLowerCase().indexOf('budbee') === -1);
				if (otherShippingMethods.length > 0) dispatch(changeShippingMethod(otherShippingMethods[0].shippingMethod));
			}
			dispatch({ type: DELIVERY_PROCEED });
			return dispatch({ type: UPDATE_BUDBEE, success, errors });
		}).catch((err) => console.log(err));
	}
};

export const changeAddressInput = (group, id, value) => ({ type: UPDATE_ADDRESS, group, id, value });

export const booleanChange = (id) => ({ type: BOOLEAN_CHANGE, id });

export const addVoucher = () => (dispatch, getState) => {
	const currentValue = getState().checkout.vouchers.currentValue;
	const { token } = getState().checkout.selection;

	if (fieldValidation(currentValue, 'voucher')) return Promise.resolve(dispatch({ type: VOUCHER_ERROR }));

	return fetchCheckout('/vouchers', token, { voucher: currentValue }, 'POST').then((data) => {
		if (data.errors) {
			setTimeout(() => {
				dispatch({ type: VOUCHER_ERROR });
			}, 1500);
			return dispatch({ type: VOUCHER_ERROR });
		}

		dispatch({ type: VOUCHER_INPUT, value: '' });
		return dispatch({ type: UPDATE_SELECTION, data });
	}).catch((err) => console.log('DEBUG: err', err));
};

export const removeVoucher = (voucherId) => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	return fetchCheckout(`/vouchers/${voucherId}`, token, {}, 'DELETE').then((data) => (
		dispatch({ type: UPDATE_SELECTION, data })
	)).catch((err) => console.log('DEBUG: err', err));
};

export const voucherInputChange = (value) => ({ type: VOUCHER_INPUT, value });

export const initiatedScript = () => ({ type: INITIATED_SCRIPT });

export const initiateCaptcha = () => (dispatch, getState) => {
	const { selection = {} } = getState().checkout;
	const $captchaContainer = document.getElementById('js-captchaContainer');

	if (selection?.captcha?.script && $captchaContainer.innerHTML === '') {
		const captchaResolvedPromise = new Promise((resolve, reject) => {
			addEvent(document, 'centra_captcha_result.captchaCheckout', (handler) => {
				console.log('DEBUG: verified', handler.detail?.verified);
				if (handler.detail?.verified) {
					return resolve(true);
				}
				return reject(new Error('Captcha failed'));
			});
		});

		$captchaContainer.innerHTML = selection.captcha.script;
		evaluate($captchaContainer);
		if (window.captchaCallback) window.captchaCallback();

		return captchaResolvedPromise;
	}

	return Promise.resolve();
};

export const initiateKlarna = () => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	const { setup } = getState().environment;
	const paymentData = {};
	paymentData.paymentFailedPage = `${window.location.origin}${prefix(setup, 'selection/failed')}`;
	paymentData.paymentReturnPage = `${window.location.origin}${prefix(setup, 'selection/receipt')}`;
	paymentData.termsAndConditions = true;

	return fetchCheckout('/payment', token, paymentData, 'POST').then((data) => (
		dispatch({ type: 'INITIATE_SELECTION', data })
	)).catch((err) => console.log('DEBUG: err', err));
};

let ingridReloadedTotal = 0;

const ingridUpdate = (event) => (dispatch, getState) => {
	const { selection, payment, useShippingAddress } = getState().checkout;
	const { setup } = getState().environment;
	const { termsAndConditions, ...paymentData } = removeInvalidPaymentFields(setup, payment, useShippingAddress);

	if (ingridReloadedTotal % 10 === 0 && ingridReloadedTotal > 0) {
		// Ingrid reloaded more than twice
		console.log('DEBUG: Ingrid reloaded more than 10x', ingridReloadedTotal);
		Sentry.withScope((scope) => {
			scope.setContext('customer', { token: selection.token, ingridReloadedTotal });
			Sentry.captureMessage('INGRID RELOADING');
		});
		if (ingridReloadedTotal >= 50) {
			if (window.confirm('An error has occurred on the page and needs to be reloaded. Your information will be saved')) {
				window.location = window.location.href;
			}
		}
	}

	ingridReloadedTotal += 1;

	return fetchCheckout('/payment-fields', selection.token, { ...event.detail, ...paymentData }, 'PUT').then((data) => {
		dispatch({ type: UPDATE_SELECTION, data });

		return dispatch({ type: PAYMENT_RETURN, data, suspendIgnore: event.detail.additionalFields.suspendIgnore });
	}).catch((err) => {
		console.log('DEBUG: err', err);
	});
};

export const ingridLoaded = () => (dispatch) => {
	removeEvent(document, 'centra_checkout_callback.ingridCheckout');
	addEvent(document, 'centra_checkout_callback.ingridCheckout', (event) => {
		dispatch(ingridUpdate(event));
	});

	return ({ type: INGRID_LOADED });
};

export const ingridUnloaded = () => {
	removeEvent(document, 'centra_checkout_callback.ingridCheckout');
	return ({ type: INGRID_UNLOADED });
};

export const klarnaLoaded = () => ({ type: KLARNA_LOADED });

export const klarnaUnloaded = () => ({ type: KLARNA_UNLOADED });

export const updateKlarna = () => (dispatch, getState) => {
	const { selection, payment, useShippingAddress } = getState().checkout;
	const { setup } = getState().environment;
	const { termsAndConditions, ...paymentData } = removeInvalidPaymentFields(setup, payment, useShippingAddress);

	if (window.CentraCheckout) {
		window.CentraCheckout.suspend();
	}

	return fetchCheckout('/payment-fields', selection.token, paymentData, 'PUT').then((data) => (
		dispatch({ type: PAYMENT_RETURN, data })
	)).catch((err) => {
		console.log('DEBUG: err', err);
	});
};

export const clearKlarna = () => ({ type: PAYMENT_RETURN });

const stripeIntentsUpdate = (event, responseType) => (dispatch) => {
	const { shippingCountry, shippingState, shippingMethod } = event.detail;

	const selectionUpdated = (response) => {
		const data = response.data;

		let returnObject = {};
		if (data && data.selection && data.selection.totals) {
			returnObject = {
				country: data.location.country,
				currency: data.selection.currency,
				currencyDenominator: data.selection.currencyFormat.denominator,
				grandTotalPriceAsNumber: data.selection.totals.grandTotalPriceAsNumber,
				shippingMethod: data.selection.shippingMethod,
				shippingMethodsAvailable: data.shippingMethods,
			};
		} else {
			returnObject = {
				error: true,
			};
		}

		const shippingUpdateEvent = new CustomEvent(responseType, { detail: returnObject }); // eslint-disable-line no-undef
		document.dispatchEvent(shippingUpdateEvent);
		return response;
	};

	if (responseType === 'centra_checkout_shipping_method_response') {
		return dispatch(changeShippingMethod(shippingMethod)).then(selectionUpdated).catch((err) => console.log('DEBUG: err', err));
	}

	if (shippingState) {
		return dispatch(changeState(shippingState, shippingCountry)).then(selectionUpdated).catch((err) => console.log('DEBUG: err', err));
	}

	return dispatch(changeCountry(shippingCountry)).then(selectionUpdated).catch((err) => console.log('DEBUG: err', err));
};

const paymentSelected = (event) => (dispatch, getState) => {
	const { selection } = getState().checkout;
	let { payment, useShippingAddress } = getState().checkout;
	const { setup } = getState().environment;
	const { paymentMethodSpecificFields, paymentMethod, responseEventRequired, addressIncluded, billingAddress, shippingAddress } = event.detail;
	if (paymentMethod === 'stripe-payment-intents' && addressIncluded) {
		payment = { ...payment, address: { ...billingAddress }, shippingAddress: { ...shippingAddress } };
		useShippingAddress = true;
	}
	const paymentData = removeInvalidPaymentFields(setup, payment, useShippingAddress);
	const postData = {
		paymentMethod,
		paymentMethodSpecificFields,
		...paymentData,
	};


	// Prevent extra clicks while payment is updating.
	dispatch({ type: PAYMENT_UPDATING, isUpdating: true });

	if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
	window.dataLayer.push({ ecommerce: null });
	window.dataLayer.push({
		event: 'Pay',
		ecommerce: {
			checkout: {
				actionField: { step: 5, option: paymentMethodSpecificFields?.paymentMethod?.brand },
				products: selection.items.map((item) => {
					const localizedItem = translateObjectData(setup, item);

					return {
						name: `${localizedItem.name} ${localizedItem.variant}`,
						id: localizedItem.sku,
						sku: localizedItem.sku,
						price: localizedItem.priceEachAsNumber,
						brand: localizedItem.brand,
						category: localizedItem.category,
						variant: localizedItem.variant,
						quantity: localizedItem.quantity,
					};
				}),
			},
		},
	});

	window.dataLayer.push({ ecommerce: null });
	window.dataLayer.push({
		event: 'add_payment_info',
		ecommerce: {
			currency: setup.currency,
			value: selection.totals.grandTotalPriceAsNumber,
			coupon: selection.discounts.vouchers.reduce((acc, obj) => [...acc, obj.voucher], []).join(', '),
			payment_type: paymentMethodSpecificFields?.paymentMethod?.type || paymentMethodSpecificFields?.paymentMethod?.brand || paymentMethod,
			items: selection.items.map((item) => {
				const localizedItem = translateObjectData(setup, item);
				const itemCategory = localizedItem.category && localizedItem.category.split('/').reduce((acc, category, j) => ({ ...acc, [`item_category${j > 0 ? j + 1 : ''}`]: category }), {});

				return {
					item_id: localizedItem.sku,
					item_name: localizedItem.name,
					item_brand: localizedItem.brand,
					...(itemCategory || {}),
					item_variant: localizedItem.variant,
					price: localizedItem.priceEachAsNumber,
					quantity: localizedItem.quantity,
				};
			}),
		},
	});

	return fetchCheckout('/payment', selection.token, postData, 'POST')
		.then((response) => {
			dispatch({ type: PAYMENT_UPDATING, isUpdating: false });

			if (response.action === 'redirect') {
				window.location.href = response.url;
				return true;
			}

			if (response.errors) {
				// Payment failed for some reason, show error
				return dispatch({ type: 'SELECTION_ERROR', hasError: true });
			}

			if (response.action === 'redirect') {
				window.location.href = response.url;
				return true;
			}

			if (response.action === 'success') {
				const { language = '' } = setup;
				const extraData = {};
				if (language !== '' && language !== 'en') extraData.language = language;

				// Remove events set up earlier.
				removeEvent(document, 'centra_checkout_payment_callback.adyenDropIn');
				removeEvent(document, 'centra_checkout_shipping_address_callback.stripeIntents');
				removeEvent(document, 'centra_checkout_shipping_method_callback.stripeIntents');
				removeEvent(document, 'centra_checkout_payment_callback.stripeIntents');

				dispatch({ type: 'RECEIPT', payload: { path: 'selection/receipt', ...extraData } });
				window.scrollTo(0, 0);
				setTimeout(() => {
					dispatch({ type: 'RECEIPT_DATA', data: response });
				}, 500);
				return true;
			}

			if (responseEventRequired) {
				// action is javascript
				if (response.formFields) {
					const detail = response.formFields;
					const updateEvent = new CustomEvent('centra_checkout_payment_response', { detail }); // eslint-disable-line no-undef
					document.dispatchEvent(updateEvent);
				} else {
					return dispatch({ type: 'SELECTION_ERROR', hasError: true });
				}
			}

			return Promise.reject(new Error('Adyen Drop In listener failed.'));
		}).catch((err) => {
			console.log('DEBUG: err', err);
			dispatch({ type: PAYMENT_UPDATING, isUpdating: false });
		});
};

export const enrichProducts = (params) => ({ type: ENRICH_PRODUCTS, ...params });

export const purchase = (selectionType = 'selection', paymentInitiateOnly = false, setPaymentMethod = '') => (dispatch, getState) => {
	const selection = getState().checkout[selectionType];
	const { payment, useShippingAddress } = getState().checkout;
	const { setup } = getState().environment;
	const { token, paymentMethod } = selection;
	const { language, countries } = setup;
	const paymentData = removeInvalidPaymentFields(setup, payment, useShippingAddress, selectionType);
	const hasErrors = [];

	if (!paymentInitiateOnly) {
		if (!paymentData.address.state && countries?.[paymentData.address.country]?.states) {
			// Missing state
			dispatch({ type: 'UPDATE_ERRORS', giftCardSelection: selectionType, group: 'address', id: 'state', error: true });
			hasErrors.push('address_state');
		}

		if (hasErrors.length > 0) {
			if (selectionType === 'selection') {
				if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
				window.dataLayer.push({ ecommerce: null });
				window.dataLayer.push({
					event: 'PurchaseProceedError',
					ecommerce: {
						checkout: {
							actionField: { step: 2, option: hasErrors.join(', ') },
							products: selection.items.map((item) => ({
								name: item.name,
								id: item.sku,
								sku: item.sku,
								price: item.priceEachAsNumber,
								brand: item.brand,
								category: item.category,
								variant: item.variant,
								quantity: item.quantity,
							})),
						},
					},
				});
			}

			dispatch({ type: SELECTION_ERROR, hasError: true });

			return Promise.reject(new Error('Purchase action failed.'));
		}

		const mailChimpCookie = getCookie('newsletterCampaign', true);
		if (mailChimpCookie !== null) {
			paymentData.additionalFields = mailChimpCookie;
		}

		if (selectionType === 'selection') {
			if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
			window.dataLayer.push({ ecommerce: null });
			window.dataLayer.push({
				event: 'PurchaseProceedSuccess',
				ecommerce: {
					checkout: {
						actionField: { step: 3, option: paymentMethod },
						products: selection.items.map((item) => ({
							name: item.name,
							id: item.sku,
							sku: item.sku,
							price: item.priceEachAsNumber,
							brand: item.brand,
							category: item.category,
							variant: item.variant,
							quantity: item.quantity,
						})),
					},
				},
			});
			window.dataLayer.push({
				event: 'user_data_initiation',
				user_data: {
					email: paymentData?.address?.email,
					address: {
						first_name: paymentData?.address?.firstName,
						last_name: paymentData?.address?.lastName,
						street: paymentData?.address?.address1,
						postal_code: paymentData?.address?.zipCode,
						region: paymentData?.address?.state,
						city: paymentData?.address?.city,
						country: paymentData?.address?.country,
					},
				},
			});
		}
	} else {
		paymentData.paymentInitiateOnly = true;
		paymentData.paymentMethod = setPaymentMethod;
		paymentData.termsAndConditions = true;
	}

	return fetchCheckout('/payment', token, paymentData, 'POST').then((data) => {
		if (data.action === 'redirect') {
			// Redirect to new URL
			window.location = data.url;
			return false;
		}
		if (data.action === 'form') {
			// Return to checkout to display form

			if (data.formType === 'adyen-drop-in') {
				const $checkoutContainer = document.getElementById('adyenDropInContainer');
				$checkoutContainer.innerHTML = data.formHtml;
				evaluate($checkoutContainer);
				dispatch({ type: ADYEN_INITIATED });
				dispatch({ type: PAYMENT_UPDATING, isUpdating: false });

				removeEvent(document, 'centra_checkout_payment_callback.adyenDropIn');
				addEvent(document, 'centra_checkout_payment_callback.adyenDropIn', (event) => {
					if (event.detail.paymentMethod.indexOf(paymentMethodsMapping.adyenDropIn) !== -1) dispatch(paymentSelected(event));
				});
				return false;
			}

			if (data.formType === 'stripe-checkout') {
				const $checkoutContainer = document.getElementById('stripeCheckoutContainer');
				$checkoutContainer.innerHTML = data.formHtml;
				evaluate($checkoutContainer);
				dispatch({ type: STRIPE_INITIATED });
				dispatch({ type: PAYMENT_UPDATING, isUpdating: false });
				return false;
			}

			if (data.formType === 'stripe-payment-intents') {
				const $checkoutContainer = document.getElementById('stripePaymentIntentsContainer');
				window.stripeRequestButtonSelector = '#stripeCheckoutButton';
				$checkoutContainer.innerHTML = data.formHtml;
				evaluate($checkoutContainer);
				dispatch({ type: STRIPE_PAYMENT_INTENTS_INITIATED });
				dispatch({ type: PAYMENT_UPDATING, isUpdating: false });

				removeEvent(document, 'centra_checkout_shipping_address_callback.stripeIntents');
				addEvent(document, 'centra_checkout_shipping_address_callback.stripeIntents', (event) => (
					dispatch(stripeIntentsUpdate(event, 'centra_checkout_shipping_address_response'))
				));
				removeEvent(document, 'centra_checkout_shipping_method_callback.stripeIntents');
				addEvent(document, 'centra_checkout_shipping_method_callback.stripeIntents', (event) => (
					dispatch(stripeIntentsUpdate(event, 'centra_checkout_shipping_method_response'))
				));
				removeEvent(document, 'centra_checkout_payment_callback.stripeIntents');
				addEvent(document, 'centra_checkout_payment_callback.stripeIntents', (event) => {
					if (event.detail.paymentMethod.indexOf(paymentMethodsMapping.stripePaymentIntents) !== -1) dispatch(paymentSelected(event));
				});
				return false;
			}

			const d = document.createElement('div');
			d.innerHTML = data.formHtml;
			document.body.appendChild(d);
			d.querySelector('form').submit();
			return false;
		}
		if (data.action === 'success') {
			// Purchase complete navigate to receipt.
			dispatch({ type: 'RECEIPT', payload: { language } });
			window.scrollTo(0, 0);
			setTimeout(() => {
				dispatch({ type: 'RECEIPT_DATA', data });
			}, 500);
			return true;
		}
		if (data.errors && data.errors.stock) {
			dispatch({ type: UPDATE_SELECTION, data, giftCardSelection: selectionType });
			dispatch({ type: PAYMENT_UPDATING, isUpdating: false });
			return dispatch({ type: SELECTION_ERROR_STOCK, hasError: true });
		}
		return dispatch({ type: SELECTION_ERROR, hasError: true });
	}).catch((err) => console.log('DEBUG: err', err));
};

export const updatePaymentMethod = (selectionType) => (dispatch, getState) => {
	const { adyenInitiated = false, stripeInitiated = false, stripePaymentIntentsInitiated = false } = getState().checkout;
	if (adyenInitiated || stripeInitiated || stripePaymentIntentsInitiated) {
		dispatch({ type: PAYMENT_UPDATING, isUpdating: true });
		dispatch(purchase(selectionType));
	}
};

const evaluate = (subtree, context = window) => {
	function getSnippetType(script) {
		return script.type || '';
	}
	function getRawSnippet(script) {
		return script.text || script.textContent || script.innerHTML || '';
	}

	if (subtree) {
		try {
			const scripts = subtree.getElementsByTagName('script');
			const scriptsAsArray = Array.from(scripts).filter((script) => getSnippetType(script) !== 'application/json');

			scriptsAsArray.forEach((script) => {
				const unescaped = getRawSnippet(script).replace(/\\/gi, '');
				eval.call(context, unescaped); // eslint-disable-line no-eval
			});
		} catch (error) {
			console.error('Caught an error in evaluate function:', error);
		}
	}
};

export const destroySelection = (selectionType = 'selection') => (dispatch, getState) => {
	const { country, countries } = getState().environment.setup;
	const defaultCountrySettings = countries[country];
	// Clear possible campaign market/pricelist to keep in sync with Centra.
	dispatch({ type: 'UPDATE_COUNTRY', country, currency: defaultCountrySettings.currency, market: parseInt(defaultCountrySettings.market, 10), pricelist: parseInt(defaultCountrySettings.pricelist, 10) });
	dispatch({ type: 'RETRIEVE_PRODUCT_CACHE' });

	dispatch({ type: DESTROY_SELECTION, giftCardSelection: selectionType });
};
